/**
 * Salutation from a guessed gender (PREC-4416, PREC-4417).
 *
 * A lead's gender is a guess with a confidence. "Herr"/"Frau" (or "Mr."/"Ms.") is written only
 * when the guess is male or female at {@link GENDER_CONFIRMED_THRESHOLD}% or more; anything else
 * is addressed by first name. The message variables, the list/Contacts columns, the CSV export and
 * the Lemlist push all answer from here, so the table shows what the message will say.
 */
export const GENDER_CONFIRMED_THRESHOLD = 90;

export type SalutationLanguage = "en" | "de";

export interface SalutationSubject {
	gender?: string | null;
	genderProbability?: number | string | null;
	firstName?: string | null;
}

const TITLES: Record<SalutationLanguage, Record<"male" | "female", string>> = {
	en: { male: "Mr.", female: "Ms." },
	de: { male: "Herr", female: "Frau" },
};

/** Male or female at the confirmed threshold. A probability may arrive as a string from raw SQL. */
export function isGenderConfirmed(
	gender: string | null | undefined,
	genderProbability: number | string | null | undefined
): gender is "male" | "female" {
	if (gender !== "male" && gender !== "female") {
		return false;
	}

	const probability =
		typeof genderProbability === "string" ? Number(genderProbability) : genderProbability;

	return typeof probability === "number" && probability >= GENDER_CONFIRMED_THRESHOLD;
}

/** "Herr"/"Frau" ("Mr."/"Ms.") for a confirmed gender, otherwise the first name ("" when none). */
export function salutationFor(subject: SalutationSubject, language: SalutationLanguage): string {
	if (isGenderConfirmed(subject.gender, subject.genderProbability)) {
		return TITLES[language][subject.gender];
	}

	return subject.firstName ?? "";
}
